import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Patient } from '@models/patient';
import { FinancialEvent, FinancialSnapshot, Order } from '@models/order';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { PaymentService, DiscountPromo } from '@services/payment.service';
import { SettingsService } from '@services/settings.service';
import { FinancialCalculator } from '@classes/financial';
import { OrderDocument } from '@classes/order-document';

@Component({
  selector: 'app-payment-details',
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.css'],
})
export class PaymentDetailsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Output() orderUpdated = new EventEmitter<Order>();
  @ViewChild('signaturePad') signaturePad: ElementRef<HTMLCanvasElement>;

  historyOpen = false;
  busy = false;
  customAmount = '';
  paymentMethod = 'CASH';
  payerName = '';
  sourceId = '';
  ackLanguage = '';
  discounts: DiscountPromo[] = [];
  availableCredit = 0;
  openInvoices: Order[] = [];
  selectedInvoiceIds: string[] = [];
  extraPatientQuery = '';
  extraPatientInvoices: Order[] = [];
  transferPatientId = '';
  transferAmount = '';
  otherCreditType = 'Custom Credit';
  otherCreditAmount = '';
  drawing = false;
  hasSignature = false;
  summaryRowList: { label: string; value: number }[] = [];
  openInvoiceList: Order[] = [];
  snapshot = FinancialCalculator.empty();

  private formSub: Subscription;
  private polSub: Subscription;
  private lastX = 0;
  private lastY = 0;
  private readonly emptyEvents: FinancialEvent[] = [];

  constructor(
    private paymentService: PaymentService,
    private settingsService: SettingsService,
    private auth: AuthService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.settingsService.loadSettings().subscribe({ error: () => {} });
    this.paymentService.listDiscounts().subscribe((discounts) => {
      this.discounts = discounts || [];
      this.recalc(false);
    });
    this.paymentService.getAckTemplate().subscribe((template) => {
      this.ackLanguage = template?.body || '';
    });
    this.bindForm();
    this.loadCredit();
    this.loadOpenInvoices();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderForm && !changes.orderForm.firstChange) {
      this.bindForm();
    }
    if (changes.patient) {
      this.loadCredit();
      this.loadOpenInvoices();
    }
  }

  ngAfterViewInit(): void {
    this.prepareCanvas();
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.polSub?.unsubscribe();
  }

  get financials() {
    return this.snapshot;
  }

  get lineItems() {
    return (this.orderForm?.get('lineItems')?.value || []).filter(
      (item) => FinancialCalculator.money(item.amount) > 0
    );
  }

  get events(): FinancialEvent[] {
    return this.orderForm?.get('financialEvents')?.value || this.emptyEvents;
  }

  get discountControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('discountId'));
  }

  get polControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('pol'));
  }

  get completionDateControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('requestedCompletionDate'));
  }

  get shippingControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('products.0.shipping'));
  }

  get acknowledged(): boolean {
    return !!this.orderForm?.get('acknowledgedAt')?.value;
  }

  get orderId(): string {
    return String(this.orderForm?.get('_id')?.value || '');
  }

  get invoiceNumber(): string {
    return String(this.orderForm?.get('invoiceNumber')?.value || '');
  }

  get isCustomRx(): boolean {
    return FinancialCalculator.isRxMerchandise(this.orderForm?.getRawValue());
  }

  get canPay(): boolean {
    if (!this.orderId || this.orderId === 'NEW') {
      return false;
    }
    if (this.isCustomRx && !this.acknowledged) {
      return false;
    }
    return FinancialCalculator.money(this.financials.balanceDue) > 0;
  }

  get combinedBalance(): number {
    const selected = this.allOpenInvoices.filter((order) =>
      this.selectedInvoiceIds.includes(String(order._id))
    );
    const others = selected.reduce(
      (sum, order) => sum + FinancialCalculator.money(order.financials?.balanceDue),
      0
    );
    if (this.selectedInvoiceIds.includes(this.orderId)) {
      return others;
    }
    return FinancialCalculator.money(this.financials.balanceDue + others);
  }

  get allOpenInvoices(): Order[] {
    return this.openInvoiceList;
  }

  formatMoney(value: any): string {
    return FinancialCalculator.formatMoney(value);
  }

  summaryRows() {
    return this.summaryRowList;
  }

  recalc(writeForm = true): void {
    if (!this.orderForm) {
      return;
    }
    const order = this.orderForm.getRawValue();
    const selected = this.discounts.find(
      (item) => String(item._id) === String(order.discountId) && item.active !== false
    );
    const snapshot = FinancialCalculator.calculate(order, {
      discount: selected,
      taxRate: this.settingsService.salesTaxRate || 0,
    });
    this.snapshot = snapshot.financials;
    this.summaryRowList = this.buildSummaryRows(snapshot.lineItems, snapshot.financials);
    if (!writeForm) {
      return;
    }
    const lineItemsCtrl = this.orderForm.get('lineItems');
    const financialsCtrl = this.orderForm.get('financials');
    if (JSON.stringify(lineItemsCtrl?.value || []) !== JSON.stringify(snapshot.lineItems)) {
      lineItemsCtrl?.setValue(snapshot.lineItems, { emitEvent: false });
    }
    if (JSON.stringify(financialsCtrl?.value || {}) !== JSON.stringify(snapshot.financials)) {
      financialsCtrl?.setValue(snapshot.financials, { emitEvent: false });
    }
  }

  payInFull(): void {
    this.submitPayment(FinancialCalculator.money(this.financials.balanceDue), true);
  }

  payCustom(): void {
    this.submitPayment(FinancialCalculator.money(this.customAmount));
  }

  submitPayment(amount: number, payInFull = false): void {
    if (!this.canPay) {
      this.notification.show('error', 'Save the order and complete acknowledgment before payment');
      return;
    }
    if (!(amount > 0)) {
      this.notification.show('error', 'Enter a payment amount');
      return;
    }
    this.busy = true;
    const selectedOthers = this.selectedInvoiceIds.filter((id) => id && id !== this.orderId);
    const payload: any = {
      amount: FinancialCalculator.money(amount),
      payInFull: !!payInFull,
      method: this.paymentMethod,
      initials: this.initials(),
      user: this.auth.currentUser?._id,
      payer: this.payerName || this.orderForm.get('payerName')?.value,
      sourceId: this.sourceId,
      notes: '',
    };

    const request =
      selectedOthers.length > 0
        ? this.paymentService.allocate({
            ...payload,
            orderIds: [this.orderId, ...selectedOthers],
          })
        : this.paymentService.charge({ ...payload, orderId: this.orderId });

    request.subscribe({
      next: (result: any) => {
        this.busy = false;
        this.customAmount = '';
        const order = result?.order || (Array.isArray(result?.orders) ? result.orders[0] : result);
        if (order) {
          this.patchOrder(order);
          this.orderUpdated.emit(order);
        }
        this.loadCredit();
        this.loadOpenInvoices();
        this.notification.show('success', 'Payment recorded');
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not record payment'
        );
      },
    });
  }

  applyCredit(): void {
    if (!this.orderId) {
      return;
    }
    this.busy = true;
    this.paymentService
      .applyStoreCredit({
        orderId: this.orderId,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.loadCredit();
          this.notification.show('success', 'Store credit applied');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show('error', err?.error?.message || 'Could not apply credit');
        },
      });
  }

  transferCredit(): void {
    const destinationPatientId = String(this.transferPatientId || '').trim();
    const amount = FinancialCalculator.money(this.transferAmount);
    if (!this.patient?._id || !destinationPatientId || !(amount > 0)) {
      this.notification.show('error', 'Enter a destination patient and amount');
      return;
    }
    this.busy = true;
    this.paymentService
      .transferStoreCredit({
        sourcePatientId: this.patient._id,
        destinationPatientId,
        amount,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
        sourceInvoice: this.invoiceNumber,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.transferAmount = '';
          this.transferPatientId = '';
          this.loadCredit();
          this.notification.show('success', 'Store credit transferred');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not transfer credit'
          );
        },
      });
  }

  applyOtherCredit(): void {
    const amount = FinancialCalculator.money(this.otherCreditAmount);
    if (!this.orderId || !(amount > 0)) {
      this.notification.show('error', 'Enter a credit amount');
      return;
    }
    this.busy = true;
    this.paymentService
      .applyStructuredCredit({
        orderId: this.orderId,
        type: this.otherCreditType,
        amount,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
        sourceInvoice: this.invoiceNumber,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.otherCreditAmount = '';
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.notification.show('success', 'Credit applied');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not apply credit'
          );
        },
      });
  }

  saveAcknowledgment(): void {
    if (!this.orderId) {
      this.notification.show('error', 'Save the order before capturing acknowledgment');
      return;
    }
    if (!this.hasSignature) {
      this.notification.show('error', 'Customer signature is required');
      return;
    }
    this.busy = true;
    this.paymentService
      .acknowledge({
        orderId: this.orderId,
        language: this.ackLanguage,
        signatureDataUrl: this.signaturePad?.nativeElement.toDataURL('image/png'),
        requestedCompletionDate: this.orderForm.get('requestedCompletionDate')?.value,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.notification.show('success', 'Order acknowledged');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not save acknowledgment'
          );
        },
      });
  }

  printSummary(): void {
    this.recalc(false);
    OrderDocument.printHtml(this.buildSummaryHtml());
  }

  emailSummary(): void {
    const email = String(this.patient?.email || '').trim();
    const f = this.financials;
    const invoice = this.invoiceNumber || 'your order';
    const body = [
      `Order summary for invoice ${invoice}.`,
      `Order total: $${this.formatMoney(f.orderTotal)}`,
      `Paid: $${this.formatMoney(f.paid)}`,
      `Balance due: $${this.formatMoney(f.balanceDue)}`,
    ].join('\n');
    OrderDocument.openMailto(email, invoice, '');
    const subject = encodeURIComponent(`Order summary ${invoice}`);
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${subject}&body=${encodeURIComponent(body)}`;
  }

  toggleInvoice(id: string, event: { checked?: boolean }): void {
    const invoiceId = String(id);
    const checked = !!event?.checked;
    if (checked) {
      if (!this.selectedInvoiceIds.includes(invoiceId)) {
        this.selectedInvoiceIds = [...this.selectedInvoiceIds, invoiceId];
      }
    } else {
      this.selectedInvoiceIds = this.selectedInvoiceIds.filter((item) => item !== invoiceId);
    }
  }

  searchExtraPatient(): void {
    const query = String(this.extraPatientQuery || '').trim();
    if (!query) {
      return;
    }
    this.paymentService.openInvoices(query).subscribe((orders) => {
      this.extraPatientInvoices = orders || [];
      this.refreshOpenInvoiceList();
    });
  }

  startDraw(event: MouseEvent | TouchEvent): void {
    this.drawing = true;
    const point = this.eventPoint(event);
    this.lastX = point.x;
    this.lastY = point.y;
  }

  moveDraw(event: MouseEvent | TouchEvent): void {
    if (!this.drawing) {
      return;
    }
    event.preventDefault();
    const point = this.eventPoint(event);
    const ctx = this.signaturePad?.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(this.lastX, this.lastY);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    this.lastX = point.x;
    this.lastY = point.y;
    this.hasSignature = true;
  }

  endDraw(): void {
    this.drawing = false;
  }

  clearSignature(): void {
    const canvas = this.signaturePad?.nativeElement;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    this.hasSignature = false;
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildSummaryHtml(): string {
    const order = this.orderForm?.getRawValue() || {};
    const productElement = order.products?.[0] || {};
    const product = productElement.product || {};
    const vendor = product.vendor?.name || product.vendor || '';
    const item = [vendor, product.model, product.color, product.size]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' · ');
    const patientName = [this.patient?.lastName, this.patient?.firstName]
      .filter(Boolean)
      .join(', ');
    const storeName = this.auth.getStoreObject?.()?.storeName || '';
    const invoice = this.invoiceNumber || order.invoiceNumber || '';
    const ackAt = order.acknowledgedAt
      ? new Date(order.acknowledgedAt).toLocaleString()
      : '';
    const rows = (this.summaryRowList || [])
      .map(
        (row) =>
          `<tr><td>${this.escapeHtml(row.label)}</td><td>$${this.formatMoney(
            row.value
          )}</td></tr>`
      )
      .join('');
    const history = (this.events || [])
      .map((event) => `<li>${this.escapeHtml(this.eventLabel(event))}</li>`)
      .join('');
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Order Summary ${this.escapeHtml(invoice)}</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color: #111; padding: 32px; }
    h1 { font-size: 20px; letter-spacing: 0.08em; margin: 0 0 8px; }
    .meta { color: #555; margin: 0 0 4px; }
    table { width: 100%; max-width: 420px; border-collapse: collapse; margin: 20px 0; }
    td { padding: 6px 0; border-bottom: 1px solid #ddd; }
    td:last-child { text-align: right; font-variant-numeric: tabular-nums; }
    ul { padding-left: 18px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>ORDER SUMMARY</h1>
  <p class="meta">${this.escapeHtml(storeName)}</p>
  <p class="meta">${this.escapeHtml(patientName)}</p>
  <p class="meta">Invoice ${this.escapeHtml(invoice)}</p>
  ${item ? `<p class="meta">${this.escapeHtml(item)}</p>` : ''}
  ${ackAt ? `<p class="meta">Acknowledged ${this.escapeHtml(ackAt)}</p>` : ''}
  <table>${rows}</table>
  ${history ? `<h2 style="font-size:14px;">Payment history</h2><ul>${history}</ul>` : ''}
</body>
</html>`;
  }

  eventLabel(event: FinancialEvent): string {
    const when = event.date ? new Date(event.date) : null;
    const stamp = when
      ? `${when.toLocaleDateString()} — ${when.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : '';
    const parts = [stamp, event.type];
    if (event.method) {
      parts.push(event.method);
    }
    if (event.amount) {
      parts.push(`$${this.formatMoney(event.amount)}`);
    }
    if (event.initials) {
      parts.push(event.initials);
    }
    if (event.payer) {
      parts.push(event.payer);
    }
    if (event.sourceInvoice) {
      parts.push(`Source Inv #${event.sourceInvoice}`);
    }
    return parts.filter(Boolean).join(' — ');
  }

  private bindForm(): void {
    this.formSub?.unsubscribe();
    this.polSub?.unsubscribe();
    if (!this.orderForm) {
      return;
    }
    this.payerName = this.orderForm.get('payerName')?.value || '';
    this.recalc(false);
    this.formSub = this.orderForm.valueChanges
      .pipe(debounceTime(0))
      .subscribe(() => this.recalc(true));
    const polControl = this.orderForm.get('pol');
    if (polControl) {
      this.polSub = polControl.valueChanges.subscribe((checked) => {
        const lensPol = this.orderForm.get('products.0.lens.POL');
        if (lensPol) {
          lensPol.setValue(checked ? 'POL' : '', { emitEvent: false });
        }
      });
    }
  }

  private loadCredit(): void {
    const patientId = this.patient?._id;
    if (!patientId) {
      this.availableCredit = 0;
      return;
    }
    this.paymentService.availableCredit(patientId).subscribe({
      next: (res: any) => {
        this.availableCredit = FinancialCalculator.money(res?.available);
      },
      error: () => {
        this.availableCredit = 0;
      },
    });
  }

  private loadOpenInvoices(): void {
    const patientId = this.patient?._id;
    if (!patientId) {
      this.openInvoices = [];
      this.refreshOpenInvoiceList();
      return;
    }
    this.paymentService.openInvoices(patientId).subscribe((orders) => {
      this.openInvoices = orders || [];
      this.refreshOpenInvoiceList();
    });
  }

  private refreshOpenInvoiceList(): void {
    const current = (this.openInvoices || []).filter(
      (order) => String(order._id) !== this.orderId
    );
    this.openInvoiceList = current.concat(this.extraPatientInvoices || []);
  }

  private patchOrder(order: Order): void {
    if (!this.orderForm || !order) {
      return;
    }
    this.orderForm.get('financials')?.setValue(order.financials, { emitEvent: false });
    this.orderForm.get('lineItems')?.setValue(order.lineItems || [], { emitEvent: false });
    this.orderForm.get('financialEvents')?.setValue(order.financialEvents || [], { emitEvent: false });
    this.orderForm.get('acknowledgedAt')?.setValue(order.acknowledgedAt || null, { emitEvent: false });
    this.orderForm.get('acknowledgment')?.setValue(order.acknowledgment || null, { emitEvent: false });
    this.orderForm.get('paidInFullDate')?.setValue(order.paidInFullDate || null, { emitEvent: false });
    this.orderForm.get('payerName')?.setValue(order.payerName || this.payerName, { emitEvent: false });
    this.recalc(false);
  }

  private buildSummaryRows(
    lineItems: { category?: string; amount?: number }[],
    financials: FinancialSnapshot
  ): { label: string; value: number }[] {
    const rows = (lineItems || [])
      .filter((item) => FinancialCalculator.money(item.amount) > 0)
      .map((item) => ({
        label: item.category,
        value: item.amount,
      }));
    rows.push({ label: 'SUBTOTAL', value: financials.subtotal });
    if (financials.discount > 0) {
      rows.push({ label: 'DISCOUNT / PROMO', value: financials.discount });
    }
    if (financials.tax > 0) {
      rows.push({ label: 'TAX', value: financials.tax });
    }
    if (financials.postage > 0) {
      rows.push({ label: 'POSTAGE', value: financials.postage });
    }
    rows.push({ label: 'ORDER TOTAL', value: financials.orderTotal });
    if (financials.creditsApplied > 0) {
      rows.push({ label: 'CREDITS', value: financials.creditsApplied });
    }
    rows.push({ label: 'PAID', value: financials.paid });
    rows.push({ label: 'BALANCE DUE', value: financials.balanceDue });
    return rows;
  }

  private asFormControl(control: AbstractControl | null | undefined): FormControl | null {
    return control ? (control as FormControl) : null;
  }

  private initials(): string {
    return (
      String(this.orderForm?.get('initials')?.value || '').trim() ||
      this.auth.currentUser?.initials ||
      ''
    );
  }

  private prepareCanvas(): void {
    const canvas = this.signaturePad?.nativeElement;
    if (!canvas) {
      return;
    }
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 320;
    const height = canvas.clientHeight || 120;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    ctx?.scale(ratio, ratio);
  }

  private eventPoint(event: MouseEvent | TouchEvent): { x: number; y: number } {
    const canvas = this.signaturePad?.nativeElement;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const touch = (event as TouchEvent).touches && (event as TouchEvent).touches[0];
    const clientX = touch ? touch.clientX : (event as MouseEvent).clientX;
    const clientY = touch ? touch.clientY : (event as MouseEvent).clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }
}
